import { IDataClientService } from '../../platform/services/data/DataClientService';
import { IAuthService } from '../../platform/services/auth/AuthService';

/**
 * Conflict types for version-based optimistic concurrency control
 */
export type ConflictType = 'VERSION_MISMATCH' | 'ENTITY_DELETED';

/**
 * Structured response for version conflicts during updates.
 * Returned from server instead of throwing errors to enable client-side conflict resolution.
 */
export interface ConflictResponse {
    __typename: 'ConflictError';
    conflictType: ConflictType;
    entityType: string;
    entityId: string;
    expectedVersion: number;
    actualVersion?: number;
    currentState?: any;
    metadata?: Record<string, any>;
}

/**
 * Options for refetching an entity's current state
 */
export interface RefetchOptions {
    entityId: string;
    dataClientService: IDataClientService;
    authService: IAuthService;
}

/**
 * Options for merging intended changes with current state
 */
export interface MergeOptions<T> {
    currentState: T;
    intendedChanges: Partial<T>;
    originalVersion: number;
}

/**
 * Handler for resolving conflicts for a specific entity type.
 * Defines how to refetch current state and merge changes.
 */
export interface ConflictHandler<T = any> {
    /**
     * Entity type this handler is for (e.g., 'Task', 'TaskListUnit')
     */
    entityType: string;

    /**
     * Refetch the current state of the entity from the server.
     */
    refetch(options: RefetchOptions): Promise<T>;

    /**
     * Merge the user's intended changes with the current server state.
     * Returns the merged entity ready for retry.
     */
    merge(options: MergeOptions<T>): Partial<T>;

    /**
     * Optional: Maximum number of retry attempts (default: 3)
     */
    maxRetries?: number;

    /**
     * Optional: Custom logic to determine if retry should be attempted.
     * Return false to abort retry and throw the conflict error.
     */
    shouldRetry?(conflict: ConflictResponse, attemptNumber: number): boolean;
}

/**
 * Checks if a GraphQL response contains a conflict error.
 */
export function isConflictResponse(response: any): response is ConflictResponse {
    return (
        response &&
        typeof response === 'object' &&
        response.__typename === 'ConflictError' &&
        response.conflictType &&
        response.entityType &&
        response.entityId !== undefined &&
        response.expectedVersion !== undefined
    );
}
